import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ButtonHLPure,
  DialogFormHLPure,
  DialogSystemGLPure,
  InputHLPure,
  TextAreaHLPure,
  DialogAddUserPure,
  DialogShareUsersPure,
  InstructionsPure,
  FileUploadMessage
} from '../../pures'
import InteractiveDialog from '../../pures/InteractiveDialog/InteractiveDialog'
import './document-panel.view.css'

/**
 * @author Edgar Escobedo
 * @description DocumentPanelView displays buttons for additional actions for documents in digital vault.
 * @typedef {Object} Props
 * @property {string} blobUrl - The Blob URL for the selected PDF, used for donlowads or previews.
 * @property {Object} documentData - The document data object containing metadata and info about the document.
 * @property {boolean} [documentData.analyzeAI] - Flag indicating if AI analysis is enabled for this document.
 * @property {string} email - The email address to send the document to.
 * @property {boolean} errorFile - Flag to display an error message if there is an error with the file.
 * @property {string} errorInput - Flag to display an error message if there is an error with the email input.
 * @property {boolean} errorInputArea - Flag indicating if there is an error in the message textarea.
 * @property {string} errorInputText - Error message for the email input.
 * @property {string} errorTextArea - Error message for the message textarea.
 * @property {Object} fileExpSelected - The selected file from an expediente (case file), if applicable.
 * @property {number} heightBody - The height of the body for the dialog.
 * @property {number} heightContainer - The height of the container for the dialog.
 * @property {Object} handlers - Object containing functions to handle various actions.
 * @property {string} messageEmail - The message to be sent with the email.
 * @property {string} messageShare - The message to be sent when sharing the document.
 * @property {Array<{ name: string, value: boolean, nameBD: string, icon: string, checked: boolean }>} permisos - Array of permissions to display buttons for actions.
 * @property {{ alerts: boolean, edit: boolean, print: boolean, download: boolean, send: boolean, share: boolean, delete: boolean, version: boolean }} permissions - Object containing permissions for actions.
 * @property {React.ReactNode} pdf - The rendered PDF reader component.
 * @property {boolean} selectedFile - Flag indicating if a file has been selected.
 * @property {string} selectedFileBase64 - Base64 string of the selected file.
 * @property {string} selectedFileName - Name of the selected file.
 * @property {function} setShowDialogVersion - Function to set the visibility of the version dialog.
 * @property {boolean} showDeleteDocument - Flag to show the delete document dialog.
 * @property {boolean} showDialogPrint - Flag to show the print dialog.
 * @property {boolean} showDialogSendEmail - Flag to show the send email dialog.
 * @property {boolean} showDialogShare - Flag to show the share dialog.
 * @property {boolean} showDialogVersion - Flag to show the version dialog.
 * @property {boolean} signedInGovo - Flag indicating if the user is signed in to Govo.
 * @property {string} userSearch - The search term for filtering users in the share dialog.
 * @property {Object} userSendGridSelected - The selected user from the send grid.
 * @property {Array} usersGrid - Array of users to display in the share dialog.
 * @property {Array} usersSendGrid - Array of users to display in the send grid.
 * @property {number} widthContainer - The width of the container for the dialog.
 * @property {boolean} showLeftDownload - Flag to show the download button on the left side.
 * @property {boolean} showShareLeft - Flag to show the share button on the left side.
 * @property {boolean} showDeleteLeft - Flag to show the delete button on the left side.
 * @param {Props} props - The component props.
 * @param {string} blobUrl - The Blob URL for the selected PDF, used for donlowads or previews.
 * @param {Object} documentData - The document data object containing metadata and info about the document.
 * @param {string} email - The email address to send the document to.
 * @param {boolean} errorFile - Flag to display an error message if there is an error with the file.
 * @param {string} errorInput - Flag to display an error message if there is an error with the email input.
 * @param {boolean} errorInputArea - Flag indicating if there is an error in the message textarea.
 * @param {string} errorInputText - Error message for the email input.
 * @param {string} errorTextArea - Error message for the message textarea.
 * @param {Object} fileExpSelected - The selected file from an expediente (case file), if applicable.
 * @param {number} heightBody - The height of the body for the dialog.
 * @param {number} heightContainer - The height of the container for the dialog.
 * @param {Object} handlers - Object containing functions to handle various actions.
 * @param {string} messageEmail - The message to be sent with the email.
 * @param {string} messageShare - The message to be sent when sharing the document.
 * @param {Array<{ name: string, value: boolean, nameBD: string, icon: string, checked: boolean }>} permisos - Array of permissions to display buttons for actions.
 * @param {{ alerts: boolean, edit: boolean, print: boolean, download: boolean, send: boolean, share: boolean, delete: boolean, version: boolean }} permissions - Object containing permissions for actions.
 * @param {React.ReactNode} pdf - The rendered PDF reader component.
 * @param {boolean} selectedFile - Flag indicating if a file has been selected.
 * @param {string} selectedFileBase64 - Base64 string of the selected file.
 * @param {string} selectedFileName - Name of the selected file.
 * @param {function} setShowDialogVersion - Function to set the visibility of the version dialog.
 * @param {boolean} showDeleteDocument - Flag to show the delete document dialog.
 * @param {boolean} showDialogPrint - Flag to show the print dialog.
 * @param {boolean} showDialogSendEmail - Flag to show the send email dialog.
 * @param {boolean} showDialogShare - Flag to show the share dialog.
 * @param {boolean} showDialogVersion - Flag to show the version dialog.
 * @param {boolean} signedInGovo - Flag indicating if the user is signed in to Govo.
 * @param {string} userSearch - The search term for filtering users in the share dialog.
 * @param {Object} userSendGridSelected - The selected user from the send grid.
 * @param {Array} usersGrid - Array of users to display in the share dialog.
 * @param {Array} usersSendGrid - Array of users to display in the send grid.
 * @param {number} widthContainer - The width of the container for the dialog.
 * @param {boolean} [showDialogAddCollaborator] - Flag to show the add collaborator dialog.
 * @param {{ email?: string, firstName?: string, lastName?: string }} [newCollaborator] - Temp state for the new collaborator form.
 * @param {{ emailInvalid?: boolean, emailExists?: boolean, domainError?: string, firstName?: boolean, lastName?: boolean }} [addCollaboratorErrors] - Optional validation flags/messages for the add collaborator dialog.
 * @param {Function} [showDialogAddInvited] - Flag to show the add invited user dialog.
 * @returns {JSX.Element} The rendered component.
 */
const DocumentPanelView = ({
  documentData,
  email,
  errorFile,
  errorInput,
  errorInputArea,
  errorInputText,
  errorTextArea,
  fileExpSelected,
  handlers,
  messageEmail,
  messageShare,
  permisos,
  permissions,
  pdf,
  currentPdf,
  selectedFile,
  selectedFileBase64,
  setShowDialogVersion,
  showDeleteDocument,
  showDialogPrint,
  showDialogSendEmail,
  showDialogShare,
  showDialogVersion,
  signedInGovo,
  userSearch,
  userSendGridSelected,
  usersGrid,
  fromDelete,
  onClickBtnActions,
  isEditing,
  onToggleEdit,
  onRequestSave,
  showLeftDownload,
  showShareLeft,
  showDeleteLeft,
  showEditLeft,
  showDialogAddCollaborator,
  newCollaborator,
  addCollaboratorErrors,
  showDialogAddInvited,
  newInvited,
  addInvitedErrors,
  isLoadingUsers,
  hasMoreUsers,
  iaAnalysisPreference,
  wasDocumentAnalyzed,
  showFileTypeWarning,
  handleCloseFileTypeWarning,
  setShowDialogEntities,
  entitiesStatus,
  handleNotYetAnalyze,
  userIsApplicant = false,
  externalDocumentsStatusCurrent,
  userIsShared = false,
}) => {
  const { t } = useTranslation()

  const currentPath = window.location.hash || window.location.pathname
  const isInBinPath = currentPath.includes('papelera') ||
    currentPath.includes('bin') ||
    currentPath.includes('/digital-vault/bin')

  const searchParams = new URLSearchParams(window.location.search)
  const hash = window.location.hash || ''
  const queryString = hash.includes('?') ? hash.split('?')[1] : ''
  const hashParams = new URLSearchParams(queryString)
  const workflowExecutionExternalId = hashParams.get('workflowExecutionExternalId') || searchParams.get('workflowExecutionExternalId')
  const isExternalUserView = !!workflowExecutionExternalId

  /**
   * Maneja el retorno desde vista de terceros regresando a Inbox
   * @function handleReturnFromExternal
   * @param {Event} e - Evento del click
   */
  const handleReturnFromExternal = (e) => {
    if (e) e.preventDefault()
    // Redirigir a Inbox cuando viene de una solicitud externa (workflowExecutionExternalId)
    window.location.hash = '#/'
  }


  const infoTypeModal = (documentData && documentData.infoType === process.env.REACT_APP_DIGITAL_VAULT_EXPEDIENTE) ? t('digitalVaultUploadView.data.exp.minus') : t('digitalVaultUploadView.data.document.minus')

  // Lógica según Tabla 1: Reglas para reprogramar análisis por IA y mostrar mensaje de versionamiento
  const shouldShowVersionMessage = () => {
    // Si no hay preferencia definida, no mostrar mensaje
    if (!iaAnalysisPreference) return false;

    const isInactive = iaAnalysisPreference === 'disabled' || iaAnalysisPreference === process.env.REACT_APP_OPTIONS_SEND_DOCUMENT_TO_OCR_ENABLE
    const isOnDemand = iaAnalysisPreference === 'onDemand' || iaAnalysisPreference === process.env.REACT_APP_OPTIONS_SEND_DOCUMENT_TO_OCR_ON_DEMAND
    const isAutomatic = iaAnalysisPreference === 'automatic' || iaAnalysisPreference === process.env.REACT_APP_OPTIONS_SEND_DOCUMENT_TO_OCR_AUTOMATIC

    // Inactiva: mostrar solo si fue analizado
    if (isInactive) {
      return wasDocumentAnalyzed === true
    }

    // Bajo demanda: mostrar solo si fue analizado
    if (isOnDemand) {
      return wasDocumentAnalyzed === true
    }

    // Automática: siempre mostrar
    if (isAutomatic) {
      return true
    }

    return false
  }

  /** @type {boolean} Indica si se debe mostrar el botón "Analizar con IA" según la tabla de reglas */
  const showAnalyzeWithIA = useMemo(() => {
    if (!documentData) return false
    return documentData.typeDocumentAnalizeAI
  }, [documentData])

  const isRequestCancelled = Number(externalDocumentsStatusCurrent) === Number(process.env.REACT_APP_ESTATUS_WORKFLOW_LOAD_FILES_GUEST_CANCELED)

  return (
    <div
      className='documentPanelActions-container'
      style={{ overflowX: 'auto', overflowY: 'hidden' }}
    >
      {isExternalUserView && !userIsApplicant ? (
        <div className='documentPanelActions-right-group'>
          <div
            className='documentPanelActions-container-btns'
            onClick={(e) => handleReturnFromExternal(e)}
          >
            <input
              type='image'
              src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_SIGN_REQUEST_BACK}`}
              height='30'
              width='30'
              alt={t('button.return')}
            />
            <h1>{t('button.return')}</h1>
          </div>
        </div>
      ) : (
        <>
          {(() => {
            const hasLeftGroup = showLeftDownload || showShareLeft || showDeleteLeft || showEditLeft
            if (!hasLeftGroup) return null
            return (
              <div className='documentPanelActions-left-group'>
                {
                  isEditing && showEditLeft && (
                    <div
                      className='documentPanelActions-container-btns'
                      onClick={() => onToggleEdit()}
                    >
                      <input
                        type='image'
                        src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_SIGN_REQUEST_REJECT}`}
                        height='30'
                        width='30'
                        alt={t('button.cancel')}
                      />
                      <h1>{t('button.cancel')}</h1>
                    </div>
                  )
                }
                {(permissions.edit && showEditLeft && !isRequestCancelled && !userIsShared) && (
                  <div
                    className='documentPanelActions-container-btns'
                    onClick={async () => {
                      if (isEditing) {
                        const ok = await (typeof onRequestSave === 'function' ? onRequestSave() : Promise.resolve(false))
                        if (ok && typeof onToggleEdit === 'function') { onToggleEdit() }
                      } else {
                        if (typeof onToggleEdit === 'function') { onToggleEdit() }
                      }
                    }}
                  >
                    <input
                      type='image'
                      src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${isEditing ? process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_SAVE : process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_EDIT}`}
                      height='30'
                      width='30'
                      alt={isEditing ? t('button.save') : t('documentPanelActions.edit')}
                    />
                    <h1>{isEditing ? t('button.save') : t('documentPanelActions.edit')}</h1>
                  </div>
                )}
                {!isEditing && !isRequestCancelled && (
                  <>
                    {showLeftDownload && (
                      <div
                        className='documentPanelActions-container-btns'
                        onClick={() => handlers.handleDownloadDoc()}
                      >
                        <input
                          type='image'
                          src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_DOWNLOAD_EXPEDIENTE}`}
                          height='30'
                          width='30'
                          alt={t('documentPanelActions.download')}
                        />
                        <h1>{t('documentPanelActions.download')}</h1>
                      </div>
                    )}
                    {userIsApplicant && showShareLeft && !isRequestCancelled && (
                      <div
                        className='documentPanelActions-container-btns'
                        onClick={(e) => handlers.handleOpenCloseDialogShare(e)}
                      >
                        <input
                          type='image'
                          src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_SHARE}`}
                          height='30'
                          width='30'
                          alt={t('documentPanelActions.share')}
                        />
                        <h1>{t('documentPanelActions.share')}</h1>
                      </div>

                    )}
                    {!userIsApplicant && showDeleteLeft && !isRequestCancelled && (
                      <div
                        className='documentPanelActions-container-btns'
                        onClick={(e) => handlers.handleShowDialogDelete(e)}
                      >
                        <input
                          type='image'
                          src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_TRASH}`}
                          height='30'
                          width='30'
                          alt={t('documentPanelActions.delete')}
                        />
                        <h1>{t('documentPanelActions.delete')}</h1>
                      </div>
                    )}
                  </>
                )}
              </div>
            )
          })()}

          <div className='documentPanelActions-right-group'>
            {(() => {
              const shouldShowBinActions = !fromDelete && ((window.location.hash || window.location.pathname).includes('papelera') ||
                (window.location.hash || window.location.pathname).includes('bin') ||
                (window.location.hash || window.location.pathname).includes('/digital-vault/bin'))

              return shouldShowBinActions && permisos && permisos.map((accion, index) => (
                <div
                  key={`bin-${index}`}
                  className='documentPanelActions-container-btns'
                  onClick={(e) => onClickBtnActions && onClickBtnActions(e, accion)}
                >
                  <input
                    type='image'
                    src={accion.icon}
                    height='30'
                    width='30'
                    alt={accion.name}
                  />
                  <h1>{accion.name}</h1>
                </div>
              ));
            })()}

            {!isEditing && !isRequestCancelled && <>
              {!userIsApplicant && !isInBinPath && showAnalyzeWithIA &&
                documentData &&
                (documentData.infoType === process.env.REACT_APP_DIGITAL_VAULT_DOCUMENT ||
                  documentData.infoType === process.env.REACT_APP_DIGITAL_VAULT_DOC_DE_EXP) && (
                  <div
                    className='documentPanelActions-container-btns'
                    onClick={() => {
                      const isPendingOrProcessing =
                        entitiesStatus === process.env.REACT_APP_EVENT_TRANSACTION_STATUS_OCR_PENDING ||
                        entitiesStatus === process.env.REACT_APP_EVENT_TRANSACTION_STATUS_OCR_PROCESSING
                      const isCompletedAnalyze = entitiesStatus === process.env.REACT_APP_EVENT_TRANSACTION_STATUS_OCR_EXTRACTED

                      if (documentData.analyzeAI && isCompletedAnalyze) {
                        return handlers.handleAnalyzeWithIA?.()
                      } else if (isPendingOrProcessing) {
                        handleNotYetAnalyze()
                      } else {
                        setShowDialogEntities()
                      }
                    }}
                  >
                    <input
                      type='image'
                      src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_FILEMANAGER_TOOLBAR_ANALYZE}`}
                      height='30'
                      width='30'
                      alt={t('documentPanelActions.analyzeIA')}
                    />
                    <h1>{t('documentPanelActions.analyzeIA')}</h1>
                  </div>
                )}
              {permissions.print && !isEditing && !isRequestCancelled && (
                <div
                  className='documentPanelActions-container-btns'
                  onClick={(e) => handlers.handleShowDialogPrint(e, true)}
                >
                  <input
                    type='image'
                    src={`${(documentData.infoType === process.env.REACT_APP_DIGITAL_VAULT_DOCUMENT) ? process.env.REACT_APP_AWS_S3_BUCKET_URL + process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_PRINT : process.env.REACT_APP_AWS_S3_BUCKET_URL + process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_PRINT_DOCUMENT}`}
                    height='30'
                    width='30'
                    alt={t('documentPanelActions.print')}
                  />
                  <h1>{t('documentPanelActions.print')}</h1>
                </div>
              )}
              {permissions.download && !isEditing && !isRequestCancelled && (
                <div
                  className='documentPanelActions-container-btns'
                  onClick={() => handlers.handleDownloadDoc(true)}
                >
                  <input
                    type='image'
                    src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_DOWNLOAD}`}
                    height='30'
                    width='30'
                    alt={t('documentPanelActions.download')}
                  />
                  <h1>{t('documentPanelActions.download')}</h1>
                </div>
              )}
              {/*   {permissions.send && !isEditing && (
            <div
              className='documentPanelActions-container-btns'
              onClick={(e) => handlers.handleShowDialog(e)}
            >
              <input
                type='image'
                src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_SEND}`}
                height='30'
                width='30'
                alt={t('documentPanelActions.send')}
              />
              <h1>{t('documentPanelActions.send')}</h1>
            </div>
          )} */}
              {userIsApplicant && permissions.share && !isEditing && !isRequestCancelled && (
                <div
                  className='documentPanelActions-container-btns'
                  onClick={(e) => handlers.handleOpenCloseDialogShare(e)}
                >
                  <input
                    type='image'
                    src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_SHARE}`}
                    height='30'
                    width='30'
                    alt={t('documentPanelActions.share')}
                  />
                  <h1>{t('documentPanelActions.share')}</h1>
                </div>
              )}
              {!userIsApplicant && (permissions.delete && !isEditing) && !isInBinPath && !isRequestCancelled && (
                <div
                  className='documentPanelActions-container-btns'
                  onClick={(e) => handlers.handleShowDialogDelete(e)}
                >
                  <input
                    type='image'
                    src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_TRASH}`}
                    height='30'
                    width='30'
                    alt={t('documentPanelActions.delete')}
                  />
                  <h1>{t('documentPanelActions.delete')}</h1>
                </div>
              )}
              {!userIsApplicant && (documentData.infoType === 'Documento' && !signedInGovo && !isEditing) && !isInBinPath && (
                <div
                  className='documentPanelActions-container-btns'
                  onClick={(e) => handlers.handleSignRequest(e)}
                >
                  <input
                    type='image'
                    src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_SIGN_REQUEST}`}
                    height='30'
                    width='30'
                    alt={t('documentPanelActions.signRequest')}
                  />
                  <h1>{t('documentPanelActions.signRequest')}</h1>
                </div>
              )}
            </>
            }
            {
              isEditing && !showEditLeft && (
                <div
                  className='documentPanelActions-container-btns'
                  onClick={() => onToggleEdit()}
                >
                  <input
                    type='image'
                    src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_SIGN_REQUEST_REJECT}`}
                    height='30'
                    width='30'
                    alt={t('button.cancel')}
                  />
                  <h1>{t('button.cancel')}</h1>
                </div>
              )
            }
            {(permissions.edit && !showEditLeft && !isRequestCancelled && !userIsShared) && (
              <div
                className='documentPanelActions-container-btns'
                onClick={async () => {
                  if (isEditing) {
                    const ok = await (typeof onRequestSave === 'function' ? onRequestSave() : Promise.resolve(false))
                    if (ok && typeof onToggleEdit === 'function') { onToggleEdit() }
                  } else {
                    if (typeof onToggleEdit === 'function') { onToggleEdit() }
                  }
                }}
              >
                <input
                  type='image'
                  src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${isEditing ? process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_SAVE : process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_EDIT}`}
                  height='30'
                  width='30'
                  alt={isEditing ? t('button.save') : t('documentPanelActions.edit')}
                />
                <h1>{isEditing ? t('button.save') : t('documentPanelActions.edit')}</h1>
              </div>
            )}
            {fromDelete && permisos && permisos.map((accion, index) => (
              <div
                key={index}
                className='documentPanelActions-container-btns'
                onClick={(e) => onClickBtnActions && onClickBtnActions(e, accion)}
              >
                <input
                  type='image'
                  src={accion.icon}
                  height='30'
                  width='30'
                  alt={accion.name}
                />
                <h1>{accion.name}</h1>
              </div>
            ))}
            {
              !isInBinPath && !isEditing && (
                <div
                  className='documentPanelActions-container-btns'
                  onClick={(e) => handlers.handleReturn(e)}
                >
                  <input
                    type='image'
                    src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_SIGN_REQUEST_BACK}`}
                    height='30'
                    width='30'
                    alt={t('button.return')}
                  />
                  <h1>{t('button.return')}</h1>
                </div>
              )
            }
          </div>
        </>
      )}

      {
        showDialogSendEmail && (
          <DialogFormHLPure
            cancel={() => handlers.handleCloseDialog()}
            titleDialog={`${t('documentPanelActions.title.dialog')} ${documentData.name}`}
            descriptionActive={false}
            descriptionDialog={t('documentPanelActions.dialog.description')}
            textBtnCancel={t('button.cancel')}
            saveBtn={t('button.send')}
            handleSubmit={(e) => handlers.handleSubmitDialog(e)}
          >
            <div className='documentPanelActions-dialog-container'>
              <InputHLPure
                typeInput='text'
                required
                labelInput={t('documentPanelActions.dialog.emails')}
                valueInput={email}
                handleChangeInput={(e) => handlers.handleChangeEmail(e)}
                tooltipShow={false}
                informationIcon={false}
                inputActive={true}
                errorInput={errorInput}
                errorInputText={errorInputText}
              />
              <div className='documentPanelActions-dialog-message'>
                <TextAreaHLPure
                  required
                  labelTextArea={t('documentPanelActions.dialog.message')}
                  valueTextData={messageEmail}
                  handleChangeTextArea={(e) => handlers.handleChangeMessageEmail(e)}
                  errorInput={errorInputArea}
                  errorTextArea={errorTextArea}
                />
              </div>
            </div>
          </DialogFormHLPure>
        )
      }
      {
        showDialogVersion && (
          <InteractiveDialog
            title={`${t('documentPanelActions.dialog.version')}: ${documentData.name}`}
            onClose={() => handlers.handleCloseDialogVersion()}
            onSave={(e) =>
              handlers.handleVersionDoc(
                e,
                setShowDialogVersion,
                documentData.id,
                fileExpSelected.id
              )
            }
            saveBtnTitle={t('upload.btn')}
            cancelBtnTitle={t('button.cancel')}
            dialogSize='XG'
            CustomButtons={
              <>
                <ButtonHLPure
                  nameBtn={t('button.cancel')}
                  onClickBtn={() => handlers.handleCloseDialogVersion()}
                />
                <ButtonHLPure
                  nameBtn={t('button.accept')}
                  primary
                  disabled={!selectedFile}
                  onClickBtn={(e) =>
                    handlers.handleVersionDoc(
                      e,
                      setShowDialogVersion,
                      documentData.id,
                      fileExpSelected.id
                    )
                  }
                />
              </>
            }
          >
            {shouldShowVersionMessage() && (
              <div className='documentPanelActions-versions-description'>
                <InstructionsPure instructions={t('documentPanelActions.dialog.version.description')} />
              </div>
            )}

            {/* Comparación lado a lado */}
            <div className='documentPanelActions-version-comparison-container'>
              <div className='documentPanelActions-version-column'>
                <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '24px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#427FBA', margin: 0 }}>
                    {t('documentPanelActions.dialog.version.current')}
                  </h3>
                </div>
                <div className='documentPanelActions-version-content'>
                  {currentPdf ? (
                    <div className='documentPanelActions-version-pdf-viewer'>
                      {currentPdf}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#999' }}>
                      {t('documentPanelActions.dialog.version.no.current')}
                    </div>
                  )}
                </div>
              </div>

              <div className='documentPanelActions-version-column'>
                <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '24px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#427FBA', margin: 0 }}>
                    {t('documentPanelActions.dialog.version.new')}
                  </h3>
                  {selectedFileBase64 && (
                    <button
                      className='button-delete'
                      onClick={() => handlers.handleDeleteFile()}
                      title={t('button.delete')}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer'
                      }}
                    >
                      <i className='k-icon k-i-delete k-i-trash' style={{ fontSize: 24 }}></i>
                    </button>
                  )}
                </div>
                <div className='documentPanelActions-version-content'>
                  {selectedFileBase64 ? (
                    <div className='documentPanelActions-version-pdf-viewer'>
                      {pdf}
                    </div>
                  ) : (
                    <FileUploadMessage
                      instructions={[
                        <>
                          {t('digitalVaultUploadView.instructions.1.document')}{' '}
                          <strong>{t('digitalVaultUploadView.instructions.1.document_strong')}</strong>{' '}
                          {t('digitalVaultUploadView.instructions.1.document_2')}
                        </>,
                        <>
                          {t('digitalVaultUploadView.instructions.2.document')}{' '}
                          <strong>{t('digitalVaultUploadView.instructions.2.document_strong')}</strong>.
                        </>,
                        t('digitalVaultUploadView.instructions.3.document')
                      ]}
                      uploadText={t('digitalVaultUploadView.upload.file')}
                      onChange={(e) => handlers.handleFile(e)}
                      multiple={false}
                      acceptFiles={'application/pdf,image/jpeg,image/jpg,image/png,image/gif,image/bmp,image/webp,audio/mpeg,audio/mp3,audio/wav,audio/ogg,video/mp4,video/mpeg,video/quicktime,video/x-msvideo,video/webm'}
                      hasError={errorFile}
                      inputId='file-upload-version'
                      enableDragAndDrop={true}
                    />
                  )}
                </div>
              </div>
            </div>
          </InteractiveDialog>
        )
      }
      {
        !showDialogAddCollaborator && !showDialogAddInvited && showDialogShare && (
          <DialogShareUsersPure
            t={t}
            documentData={documentData}
            usersGrid={usersGrid}
            permisos={permisos}
            userSearch={userSearch}
            messageShare={messageShare}
            handlers={handlers}
            userSendGridSelected={userSendGridSelected}
            isLoadingUsers={isLoadingUsers}
            hasMoreUsers={hasMoreUsers}
          />
        )
      }
      {
        showDialogAddCollaborator && (
          <DialogAddUserPure
            showDialog={showDialogAddCollaborator}
            type='collaborator'
            t={t}
            newUser={newCollaborator}
            userErrors={addCollaboratorErrors}
            handlers={handlers}
          />
        )
      }
      {
        showDialogAddInvited && (
          <DialogAddUserPure
            showDialog={showDialogAddInvited}
            type='invited'
            t={t}
            newUser={newInvited}
            userErrors={addInvitedErrors}
            handlers={handlers}
          />
        )
      }
      {
        showDeleteDocument && (
          <DialogSystemGLPure
            showConfirm={true}
            confirmTitle={t('documentPanelActions.title.dialog.delete')}
            confirmMessage={t('documentPanelActions.question.dialog.delete', { infoType: infoTypeModal })}
            labelCancelConfirmation={t('button.cancel')}
            labelSubmitConfirmation={t('documentPanelActions.question.dialog.delete.btn')}
            handleConfirmDeleteFlowClose={() => handlers.handleCloseDialogDelete()}
            handleConfirmDeleteFlowSubmit={() => handlers.handleDeleteDocument()}
          />
        )
      }
      {
        showDialogPrint && (
          <DialogFormHLPure
            titleDialog={`Documento a imprimir: ${documentData.name}`}
            cancel={() => handlers.handleCloseDialogPrint()}
          >
            <div className='documentPanelActions-print-dialog'>
              <div className='documentPanelActions-print-dialog-embed'>
                <embed
                  id='fileDoc'
                  type='application/pdf'
                  width='450'
                  height='400'
                />
              </div>
              <div className='documentPanelActions-print-dialog-btn-cancel'>
                <ButtonHLPure
                  nameBtn={'Cerrar'}
                  onClickBtn={() => handlers.handleCloseDialogPrint()}
                />
              </div>
            </div>
          </DialogFormHLPure>
        )
      }
      {
        showFileTypeWarning && (
          <DialogSystemGLPure
            isWarnign={true}
            messageWarning={t('documentPanelActions.warning.fileType.notAllowed')}
            handleWarningClose={handleCloseFileTypeWarning}
            handleWarningSubmit={handleCloseFileTypeWarning}
            showBtnSubmitWarning={true}
            labelWarningBtnCancel={t('button.accept')}
          />
        )
      }
    </div >
  )
}

export default DocumentPanelView